/**
 * Helpdesk API Service
 * ====================
 * Centralizes all helpdesk API calls in one place.
 * Import this instead of calling api.get/post directly from components.
 *
 * Usage:
 *   import helpdeskApi from '@/api/helpdesk';
 *   const tickets = await helpdeskApi.getTickets({ status: 'new' });
 */
import api from './api';

const helpdeskApi = {
  // ─── Tickets ─────────────────────────────────────────────────────────────
  getTickets: (params = {}) => api.get('/helpdesk/tickets/', { params }),
  getTicket: (id) => api.get(`/helpdesk/tickets/${id}/`),
  createTicket: (data) => api.post('/helpdesk/tickets/', data),
  updateTicket: (id, data) => api.patch(`/helpdesk/tickets/${id}/`, data),
  deleteTicket: (id) => api.delete(`/helpdesk/tickets/${id}/`),

  // Ticket actions
  archiveTicket: (id) => api.post(`/helpdesk/tickets/${id}/archive/`),
  unarchiveTicket: (id) => api.post(`/helpdesk/tickets/${id}/unarchive/`),
  assignTicket: (id, userId) => api.post(`/helpdesk/tickets/${id}/assign/`, { user_id: userId }),
  changeStatus: (id, status) => api.post(`/helpdesk/tickets/${id}/change_status/`, { status }),
  mergeTickets: (primaryId, secondaryId) =>
    api.post(`/helpdesk/tickets/${primaryId}/merge/`, { secondary_ticket_id: secondaryId }),
  bulkDeleteTickets: (ids) => api.post('/helpdesk/tickets/bulk_delete/', { ticket_ids: ids }),

  // Ticket comments
  getComments: (ticketId) => api.get(`/helpdesk/tickets/${ticketId}/comments/`),
  addComment: (ticketId, comment) =>
    api.post(`/helpdesk/tickets/${ticketId}/add_comment/`, { comment }),

  // Stats
  getStatusCounts: (params = {}) => api.get('/helpdesk/tickets/status_counts/', { params }),
  getStatusSummary: () => api.get('/helpdesk/tickets/status_summary/'),
  getDashboardStats: (params = {}) => api.get('/helpdesk/tickets/dashboard_stats/', { params }),

  // ─── Categories ──────────────────────────────────────────────────────────
  getCategories: (params = {}) => api.get('/helpdesk/categories/', { params }),
  createCategory: (data) => api.post('/helpdesk/categories/', data),
  updateCategory: (id, data) => api.patch(`/helpdesk/categories/${id}/`, data),
  deleteCategory: (id) => api.delete(`/helpdesk/categories/${id}/`),
  archiveCategory: (id) => api.post(`/helpdesk/categories/${id}/archive/`),

  // ─── Teams ───────────────────────────────────────────────────────────────
  getTeams: (params = {}) => api.get('/helpdesk/teams/', { params }),
  getTeam: (id) => api.get(`/helpdesk/teams/${id}/`),
  createTeam: (data) => api.post('/helpdesk/teams/', data),
  updateTeam: (id, data) => api.patch(`/helpdesk/teams/${id}/`, data),
  deleteTeam: (id) => api.delete(`/helpdesk/teams/${id}/`),
  addTeamMember: (teamId, userId) =>
    api.post(`/helpdesk/teams/${teamId}/add_member/`, { user_id: userId }),
  removeTeamMember: (teamId, userId) =>
    api.post(`/helpdesk/teams/${teamId}/remove_member/`, { user_id: userId }),

  // ─── SLA Policies ────────────────────────────────────────────────────────
  getSLAPolicies: (params = {}) => api.get('/helpdesk/sla-policies/', { params }),
  getSLAPolicy: (id) => api.get(`/helpdesk/sla-policies/${id}/`),
  createSLAPolicy: (data) => api.post('/helpdesk/sla-policies/', data),
  updateSLAPolicy: (id, data) => api.patch(`/helpdesk/sla-policies/${id}/`, data),
  deleteSLAPolicy: (id) => api.delete(`/helpdesk/sla-policies/${id}/`),
  activateSLAPolicy: (id) => api.post(`/helpdesk/sla-policies/${id}/activate/`),
  deactivateSLAPolicy: (id) => api.post(`/helpdesk/sla-policies/${id}/deactivate/`),

  // ─── WorkLogs ────────────────────────────────────────────────────────────
  getWorkLogs: (params = {}) => api.get('/helpdesk/worklogs/', { params }),
  createWorkLog: (data) => api.post('/helpdesk/worklogs/', data),
  updateWorkLog: (id, data) => api.patch(`/helpdesk/worklogs/${id}/`, data),
  deleteWorkLog: (id) => api.delete(`/helpdesk/worklogs/${id}/`),

  // ─── Ticket Tasks ─────────────────────────────────────────────────────────
  getTicketTasks: (params = {}) => api.get('/helpdesk/ticket-tasks/', { params }),
  createTicketTask: (data) => api.post('/helpdesk/ticket-tasks/', data),
  updateTicketTask: (id, data) => api.patch(`/helpdesk/ticket-tasks/${id}/`, data),
  deleteTicketTask: (id) => api.delete(`/helpdesk/ticket-tasks/${id}/`),

  // ─── Ticket Notes ─────────────────────────────────────────────────────────
  getTicketNotes: (params = {}) => api.get('/helpdesk/ticket-notes/', { params }),
  createTicketNote: (data) => api.post('/helpdesk/ticket-notes/', data),
  updateTicketNote: (id, data) => api.patch(`/helpdesk/ticket-notes/${id}/`, data),
  deleteTicketNote: (id) => api.delete(`/helpdesk/ticket-notes/${id}/`),
};

export default helpdeskApi;
